import type { Bitmap } from "../bitmap/types.ts";
import type { GenericSize } from "../model/index.ts";
import type { DrawBounds } from "./draw-bounds.ts";
import getRenderMeasurements from "./get-render-measurements.ts";

// Key names are same values, but it's to help guide the users of the function
type Options = {
	readonly buildMap: Pick<Bitmap, "width" | "height">;
} & (
	| {
			readonly drawBounds: Pick<DrawBounds, "width" | "height">;
	  }
	| {
			readonly canvas: GenericSize<number>;
	  }
);

// The smallest utility canvas that can render this build into this area. The
// pixel path (renderPictureAsPixels) needs the whole build to fit, the brick
// path (renderPictureAsBricks) needs one brick sprite to fit.
function getRequiredUtilityCanvasSize(options: Options): GenericSize<number> {
	const { ceilBrickSizePixels } = getRenderMeasurements(
		"drawBounds" in options ? options.drawBounds.width : options.canvas.width,
		"drawBounds" in options ? options.drawBounds.height : options.canvas.height,
		options.buildMap.width,
		options.buildMap.height,
	);
	return {
		width: Math.max(options.buildMap.width, ceilBrickSizePixels),
		height: Math.max(options.buildMap.height, ceilBrickSizePixels),
	};
}

function canUtilityCanvasRender(
	utilityCanvas: GenericSize<number>,
	options: Options,
): boolean {
	const required = getRequiredUtilityCanvasSize(options);
	return (
		utilityCanvas.width >= required.width &&
		utilityCanvas.height >= required.height
	);
}

function createUtilityCanvas<T>(
	options: Options,
	factory: (width: number, height: number) => T,
) {
	const { width, height } = getRequiredUtilityCanvasSize(options);
	return factory(width, height);
}

export default createUtilityCanvas;
export { getRequiredUtilityCanvasSize, canUtilityCanvasRender };
